import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Send, MessageCircle, Store, User, Image as ImageIcon, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const timeFmt = (s) => { try { return new Date(s).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function Chat() {
  const location = useLocation();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [myRole, setMyRole] = useState("buyer");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();
  const imgRef = useRef();

  const loadConvs = useCallback(async () => {
    try { const { data } = await api.get("/chat/conversations"); setConvs(data); return data; }
    catch { return []; }
  }, []);

  const openConv = useCallback(async (id) => {
    setActive(id);
    try {
      const { data } = await api.get(`/chat/conversations/${id}/messages`);
      setMessages(data.messages); setMyRole(data.my_role);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  // initial: load list, auto-start or auto-open from navigation state
  useEffect(() => {
    (async () => {
      const list = await loadConvs();
      const st = location.state || {};
      if (st.conversationId) { openConv(st.conversationId); return; }
      if (st.start) {
        try {
          const { data } = await api.post("/chat/start", st.start);
          await loadConvs(); openConv(data.id);
        } catch (e) { toast.error(errMsg(e)); }
      } else if (list.length) { openConv(list[0].id); }
    })();
  }, [loadConvs, openConv, location.state]);

  // poll active thread
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(async () => {
      try { const { data } = await api.get(`/chat/conversations/${active}/messages`); setMessages(data.messages); } catch {}
    }, 4000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const t = text.trim(); setText("");
    try {
      const { data } = await api.post(`/chat/conversations/${active}/messages`, { text: t });
      setMessages((m) => [...m, data]); loadConvs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const activeConv = convs.find((c) => c.id === active);

  const sendImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !active) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("File melebihi batas maksimal 50MB");
      return;
    }
    setSending(true);
    try {
      const fileToUpload = await compressImage(f);
      const fd = new FormData(); fd.append("file", fileToUpload);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}/api/files/${data.path}`;
      const { data: msg } = await api.post(`/chat/conversations/${active}/messages`, { image_url: url });
      setMessages((m) => [...m, msg]); loadConvs();
    } catch (e2) { toast.error(errMsg(e2, "Gagal mengirim foto")); }
    finally { setSending(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold text-rl-ink mb-6">Pesan</h1>
        <div className="grid md:grid-cols-[300px_1fr] gap-4 rounded-2xl border border-rl-border bg-white overflow-hidden min-h-[540px]">
          {/* Conversation list */}
          <div className="border-r border-rl-border overflow-y-auto max-h-[540px]">
            {convs.length === 0 && <div className="p-6 text-center text-rl-muted text-sm">Belum ada percakapan.</div>}
            {convs.map((c) => {
              const unread = c.role === "buyer" ? c.buyer_unread : c.seller_unread;
              const title = c.role === "buyer" ? c.store_name : c.buyer_name;
              return (
                <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => openConv(c.id)}
                  className={`w-full text-left px-4 py-3 border-b border-rl-border hover:bg-rl-bg2 transition-colors ${active === c.id ? "bg-rl-bg2" : ""}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-rl-secondary/10 grid place-items-center shrink-0">
                      {c.role === "buyer" ? <Store className="w-4 h-4 text-rl-secondary" /> : <User className="w-4 h-4 text-rl-secondary" />}</div>
                    <span className="font-semibold text-sm text-rl-ink truncate flex-1">{title}</span>
                    {unread > 0 && <span className="bg-rl-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">{unread}</span>}
                  </div>
                  {c.product_title && <div className="text-[11px] text-rl-muted mt-1 truncate pl-10">{c.product_title}</div>}
                  {c.last_message && <div className="text-xs text-rl-ink2 mt-0.5 truncate pl-10">{c.last_message}</div>}
                </button>
              );
            })}
          </div>

          {/* Thread */}
          <div className="flex flex-col max-h-[540px]">
            {activeConv ? (
              <>
                <div className="px-5 py-3 border-b border-rl-border flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-rl-primary" />
                  <span className="font-bold text-rl-ink">{myRole === "buyer" ? activeConv.store_name : activeConv.buyer_name}</span>
                  {activeConv.product_title && <span className="text-xs text-rl-muted ml-2 truncate">· {activeConv.product_title}</span>}
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-rl-bg2">
                  {messages.map((m) => {
                    const mine = m.sender_role === myRole;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-rl-primary text-white rounded-br-sm" : "bg-white border border-rl-border text-rl-ink rounded-bl-sm"}`}>
                          {m.image_url && <img src={m.image_url} alt="foto" onClick={() => window.open(m.image_url, "_blank")} className="rounded-lg max-h-52 mb-1 cursor-pointer object-cover" />}
                          {m.text && <div>{m.text}</div>}
                          <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-rl-muted"}`}>{timeFmt(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <div className="p-3 border-t border-rl-border flex gap-2 items-center">
                  <button data-testid="chat-image-btn" onClick={() => imgRef.current?.click()} disabled={sending}
                    className="rounded-full border border-rl-border hover:border-rl-primary text-rl-ink2 hover:text-rl-primary w-10 h-10 grid place-items-center transition-colors disabled:opacity-50">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}</button>
                  <input ref={imgRef} type="file" accept="image/*" hidden onChange={sendImage} data-testid="chat-image-input" />
                  <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Tulis pesan..."
                    className="flex-1 rounded-full border border-rl-border px-4 py-2.5 text-sm outline-none focus:border-rl-primary" />
                  <button data-testid="chat-send-btn" onClick={send} disabled={!text.trim()}
                    className="rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white px-5 h-10 grid place-items-center disabled:opacity-50 transition-colors">
                    <Send className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <div className="flex-1 grid place-items-center text-rl-muted"><div className="text-center"><MessageCircle className="w-10 h-10 mx-auto opacity-40" /><p className="mt-2 text-sm">Pilih percakapan.</p></div></div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
